import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import FrontView2D from "./FrontView2D";
import TopView2D from "./TopView2D";
import AddIcon from "@material-ui/icons/Add";
import IconButton from "@material-ui/core/IconButton";
import {
  addNewField,
  setHideTopView,
  updateGlobalParameter,
  updateOverallWidth,
} from "../reducers/Artus/Actions";
import { get, last, maxBy, sum } from "lodash";
import arrowUp from "../assets/arrowUp.svg";
import arrowDown from "../assets/arrowDown.svg";
import { useTranslation } from "react-i18next";
import { Switch } from "@material-ui/core";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: "auto",
    padding: "0 20px",
    position: "relative",
    width: "100%",
  },
  viewSwitch: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& .MuiIconButton-root": {
      padding: "9px !important",
    },
  },
  switchThumbColor: {
    "& .MuiIconButton-root": {
      color: "#fafafa",
    },
  },
  col: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
    paddingBottom: 40,
    color: "#3f3f3f",
  },
  infoContainer: {
    position: "relative",
    height: 30,
  },
  paletteInfo: {
    textAlign: "start",
    fontWeight: "bold",
  },
  supportBar: { position: "relative" },
  colorBar: {
    opacity: 0.5,
  },
  barImage: {
    width: "100%",
    position: "absolute",
    bottom: 0,
    zIndex: -1,
    opacity: 0.3,
    left: -5,
    display: "none",
  },
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: 100,
  },
  flexRow: {
    display: "flex",
    alignItems: "center",
    paddingTop: 40,
    color: "#3f3f3f",
  },
  viewlabel: {
    marginLeft: 100,
  },
  maxFrameHeight: {
    alignSelf: "flex-start",
    marginRight: 40,
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    minWidth: 60,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    display: "flex",
  },
  maxFrameHeightTop: {
    marginRight: 40,
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    minWidth: 60,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    display: "flex",
  },
  iconButtonContainer: {
    width: "100%",
    bottom: "50%",
    position: "absolute",
    display: "flex",
    alignItems: "center",
    minWidth: 200,
    justifyContent: "center",
  },

  iconButton: {
    backgroundColor: "#c5da86",
    boxShadow: "0px 2px 4px #00000080",
    borderWidth: 1,
    borderColor: "#c5da86",
    borderRadius: 2,
    padding: "4px",
    "&:hover": {
      backgroundColor: "#c5da86",
    },
  },
  overallWidth: {
    marginTop: 55,
    alignSelf: "center",
    width: "100%",
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    display: "flex",
  },
  verticalArrow: {
    display: "flex",
    alignItems: "flex-start",
    height: "50%",
    flexDirection: "column",
  },
  arrow: {
    display: "flex",
    alignItems: "flex-start",
    width: "50%",
  },
}));

const Visualization2D = (props) => {
  const dispatch = useDispatch();
  const previewContainerRef = useRef();
  const classes = useStyles(props);
  const { t } = useTranslation(["visualization2d"]);
  const [fieldsUpdated, setFieldsUpdated] = useState(0);
  const { configuration } = useSelector((state) => state.Configuration);
  const optionsLoaded = useSelector((state) => {
    return state.Configuration.optionsLoaded;
  });
  const { fields, scaleFactor, doubleShelf, distance, highestFrame, overallWidth, hideTopView } = useSelector(
    (state) => state.Artus
  );

  let containerRef = null;
  const [containerHeight, setContainerHeight] = useState(0);
  const [prevHighestFrameHeight, setPrevHighestFrameHeight] = useState(0);
  const handleResize = (containerRef) => {
    const extraSpace = doubleShelf ? 250 : 210;
    const maxScaleFactor = 12;
    let scales = Array.from({ length: maxScaleFactor }, (_, i) => i + 1);
    scales = scales.map((scale) => {
      const scaledHighestFrameHeight = highestFrame / scale;
      const scaledTopViewHighestFrame = doubleShelf ? 2200 / scale : 1100 / scale;
      return { scale: scale, height: scaledHighestFrameHeight + scaledTopViewHighestFrame + extraSpace };
    });
    const match = maxBy(scales, (scale) => (scale.height <= containerRef.clientHeight ? scale : null));
    dispatch(updateGlobalParameter("scaleFactor", match ? match.scale : maxScaleFactor));
    if (containerRef.clientHeight < containerRef.scrollHeight) {
      containerRef.scrollTop = containerRef.scrollHeight - containerRef.clientHeight;
    }
  };
  useEffect(() => {
    window.addEventListener("resize", (e) => {
      const ele = window.document.getElementById("containerRef");
      handleResize(ele);
    });
  }, []);
  useEffect(
    () => {
      if (
        configuration &&
        containerRef &&
        (containerHeight !== containerRef.scrollHeight || highestFrame !== prevHighestFrameHeight)
      ) {
        handleResize(containerRef);
        setContainerHeight(containerRef.scrollHeight);
        setPrevHighestFrameHeight(highestFrame);
      }
    },
    containerRef,
    highestFrame
  );
  const displayedWidth = sum(
    fields.map((field) => {
      return Number(field.width.match(/\d+/));
    })
  );

  const depth = doubleShelf ? 2200 + distance : 1100;
  const maxFrameHeight = highestFrame / scaleFactor;
  const maxHeightTopView = depth / scaleFactor;
  const fieldsLength = fields.length;
  const data = last(fields);
  const supportBars = Number(get(data, "supportBars", 3).match(/\d+/));
  const width = Number(get(data, "width", 2000).match(/\d+/));
  const scaledWidth = width / scaleFactor;
  const showSwitch = Number(scaleFactor) >= 10;
  useEffect(() => {
    previewContainerRef.current &&
      previewContainerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  }, [fieldsUpdated, scaleFactor]);

  const addField = async () => {
    setFieldsUpdated(fieldsUpdated + 1);
    await dispatch(addNewField());
    return dispatch(updateOverallWidth());
  };

  const toggleTopView = () => {
    dispatch(setHideTopView(!hideTopView));
  };

  const TopView = (props) => {
    const classes = useStyles(props);
    return (
      <div style={{ flexDirection: "column", display: hideTopView && showSwitch ? "none" : "flex" }}>
        {!showSwitch && (
          <Typography className={classes.viewlabel} style={{ marginBottom: 15 }} variant="body1">
            {t("topView")}
          </Typography>
        )}
        <div className={classes.col} style={{ paddingTop: showSwitch ? 20 : "" }}>
          <div className={classes.maxFrameHeightTop} style={{ height: maxHeightTopView }}>
            <div className={classes.verticalArrow}>
              <img src={arrowUp} />
              <div style={{ height: "100%", width: 4.5, borderRight: "1px solid #979797", marginTop: -3 }} />
            </div>
            <div style={{ marginTop: 20, marginBottom: 20 }}>{`${depth} mm`}</div>
            <div className={classes.verticalArrow}>
              <div
                style={{ height: "100%", width: 4.5, borderRight: "1px solid #979797", marginBottom: -3 }}
              />
              <img src={arrowDown} />
            </div>
          </div>
          {fields.map((field, i) => {
            field.length = fieldsLength;
            field.nextFieldHeight = field.id <= fieldsLength - 1 ? fields[field.id - 1].height : field.height;
            return (
              <TopView2D
                fields={fields}
                key={field.id}
                data={field}
                scaleFactor={scaleFactor}
                doubleShelf={doubleShelf}
                distance={distance}
                nextField={i === fields.length - 1 ? field : fields[i + 1]}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const FrontView = (props) => {
    const classes = useStyles(props);
    return (
      <div style={{ flexDirection: "column", display: "flex" }}>
        <div className={classes.col}>
          {fields.map((field, i) => {
            field.length = fieldsLength;
            field.nextFieldHeight = field.id <= fieldsLength - 1 ? fields[field.id - 1].height : field.height;
            return (
              <FrontView2D
                fields={fields}
                updateOverallWidth={updateOverallWidth}
                key={field.id}
                data={field}
                scaleFactor={scaleFactor}
                nextField={i === fields.length - 1 ? field : fields[i + 1]}
              />
            );
          })}
        </div>
        <div className={classes.overallWidth}>
          <div className={classes.arrow}>
            <img src={arrowUp} style={{ transform: "rotate(-90deg)" }} />
            <div style={{ width: "100%", height: 4.5, borderBottom: "1px solid #979797", marginLeft: -3 }} />
          </div>
          <div
            style={{ marginLeft: 20, marginRight: 20, minWidth: 80, width: 80, textAlign: "center" }}
          >{`${overallWidth} mm`}</div>
          <div className={classes.arrow}>
            <div style={{ width: "100%", height: 4.5, borderBottom: "1px solid #979797", marginRight: -3 }} />
            <img src={arrowDown} style={{ transform: "rotate(-90deg)" }} />
          </div>
        </div>
      </div>
    );
  };
  if (scaleFactor === 0) {
    return (
      <div
        className={classes.root}
        id="containerRef"
        ref={(ele) => {
          containerRef = ele;
        }}
      ></div>
    );
  }
  return (
    <>
      <div
        className={classes.root}
        id="containerRef"
        ref={(ele) => {
          containerRef = ele;
        }}
      >
        {showSwitch ? (
          <div className={classes.viewSwitch}>
            <Typography variant="body1">{t("frontView")}</Typography>
            <Switch
              classes={{
                root: classes.switchThumbColor,
                switchBase: !hideTopView ? "activeCheckedColor" : "",
                checked: !hideTopView ? "activeCheckedColor" : "",
                track: clsx(classes.colorBar, "primaryButton"),
              }}
              checked={!hideTopView}
              onChange={toggleTopView}
            />
            <Typography variant="body1">{t("topView")}</Typography>
          </div>
        ) : (
          <TopView />
        )}
        {showSwitch && !hideTopView && <TopView />}
        <div
          style={{ flexDirection: "column", display: !hideTopView ? "none" : "flex" }}
          id="screenshot-area"
        >
          {!showSwitch && (
            <Typography
              className={classes.viewlabel}
              style={{ marginBottom: -24 }}
              variant="body1"
              id="front-view-heading"
            >
              {t("frontView")}
            </Typography>
          )}
          <div className={classes.flexRow}>
            <div className={classes.maxFrameHeight} style={{ height: maxFrameHeight }}>
              <div className={classes.verticalArrow}>
                <img src={arrowUp} />
                <div
                  style={{ height: "100%", width: 4.5, borderRight: "1px solid #979797", marginTop: -3 }}
                />
              </div>

              <div style={{ marginTop: 20, marginBottom: 20 }}>{`${highestFrame} mm`}</div>
              <div className={classes.verticalArrow}>
                <div
                  style={{
                    height: "100%",
                    width: 4.5,
                    borderRight: "1px solid #979797",
                    marginBottom: -3,
                  }}
                />
                <img src={arrowDown} />
              </div>
            </div>
            <FrontView props={props} />
            <div
              id={"add-button-container"}
              className={classes.wrapper}
              style={{
                minHeight: maxFrameHeight,
                maxHeight: maxFrameHeight,
                minWidth: scaledWidth,
                width: scaledWidth,
                gridTemplateRows: `${472 / scaleFactor}px repeat(${supportBars}, 1fr)`, //444 + 28, 444 is offset for firstSupportbar, 28 is height of supportBar
              }}
            >
              {optionsLoaded && (
                <div ref={previewContainerRef}>
                  <IconButton className={classes.iconButton} onClick={addField}>
                    <AddIcon style={{ color: "#262626" }} />
                  </IconButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Visualization2D;
